import { observable, action } from 'mobx'
import { IUpdateAuditRequest, API } from '@getgreenline/homi-shared'
import { message } from 'antd'
import { parseErrorMsg } from '../../../../../utilities/helpers'
import { AuditModels, AuditProductModels } from '@getgreenline/audits'

type TAuditDiffType = 'lost' | 'added'

export class AuditDescriptionStore {
  @observable
  isTitleEditable = false

  @observable
  auditTitle: string

  @observable
  isLoading = false

  constructor(private audit: AuditModels.CreateInventoryAudit) {
    this.auditTitle = audit.title
  }

  @action
  toggleTitleEditable = () => (this.isTitleEditable = !this.isTitleEditable)

  @action
  setAuditTitle = (newTitle: string) => (this.auditTitle = newTitle)

  @action
  updateTitle = async () => {
    try {
      this.isLoading = true
      const { locationName, companyId, id: auditId } = this.audit
      const newAuditTitle =
        this.auditTitle.length === 0 ? `Audit at ${locationName}` : this.auditTitle
      const updateProductJSON: IUpdateAuditRequest = { update: { title: newAuditTitle } }
      await API.updateInventoryAudit(companyId, auditId, updateProductJSON)
      this.audit.title = newAuditTitle
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error updating your title')
    } finally {
      this.isLoading = false
      this.toggleTitleEditable()
    }
  }

  private getDiffQuantity = (AuditDiffType: TAuditDiffType): number => {
    const auditProductsNotNull = this.audit.products.filter((auditProduct) => {
      return auditProduct.enteredQuantity !== null
    })

    const differences: number[] = auditProductsNotNull.map((notNullProduct) =>
      this.getDifferencesBetweenEnteredAndExpected(notNullProduct),
    )

    const values =
      AuditDiffType === 'lost'
        ? differences.filter((diff) => diff < 0)
        : differences.filter((diff) => diff > 0)
    const sum = values.reduce((a, b) => a + b, 0)

    return Math.abs(sum)
  }

  private getDiffValue = (AuditDiffType: TAuditDiffType): number => {
    const auditProductsNotNull = this.audit.products.filter(
      (auditProduct) => auditProduct.enteredQuantity !== null,
    )

    const auditProductsFilteredByCost = auditProductsNotNull.filter((auditProduct) => {
      const totalCostDifference =
        (auditProduct.purchasePrice || 0) *
        this.getDifferencesBetweenEnteredAndExpected(auditProduct)
      return AuditDiffType === 'lost' ? totalCostDifference < 0 : totalCostDifference > 0
    })

    const sumOfCost = auditProductsFilteredByCost.reduce((cost, auditProduct) => {
      const totalCostDifference =
        (auditProduct.purchasePrice || 0) *
        this.getDifferencesBetweenEnteredAndExpected(auditProduct)
      return cost + totalCostDifference
    }, 0)

    return sumOfCost
  }

  private getDifferencesBetweenEnteredAndExpected = (
    auditProduct: AuditProductModels.EditAuditProduct,
  ): number => {
    const difference = (auditProduct.enteredQuantity || 0) - (auditProduct.expectedQuantity || 0)
    return difference
  }

  get countedCount(): number {
    return this.audit.products.filter((p) => p.enteredQuantity !== null).length
  }
  get totalCount(): number {
    return this.audit.products.length
  }

  get totalLostQuantity(): number {
    return this.getDiffQuantity('lost')
  }

  get totalAddedQuantity(): number {
    return this.getDiffQuantity('added')
  }

  get totalLostCost(): number {
    return this.getDiffValue('lost')
  }

  get totalAddedCost(): number {
    return this.getDiffValue('added')
  }

  get progressbarWidth(): string {
    return ((this.countedCount / this.totalCount) * 100).toFixed(2) + '%'
  }
}
